/**
 * Shared RBC Analytics utilities for GTM and native bridge
 */

export interface RBCUniversalEventParameters {
  loyalty_surrogate_id?: string;
  confirmation_id?: string;
  // GBV: the total booking amount shown to the user.
  value?: number;
  search_term?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
}

export interface RBCBaseEvent {
  event: string;
  page_language: string;
  lob: string;
  channel: string;
  content_group: string;
}

export interface RBCTapEvent extends RBCBaseEvent {
  version: string;
  type: "click";
  name: "tap";
  details: string;
  element_type: string;
  universalEventParameters: RBCUniversalEventParameters;
}

export interface RBCScreenViewEvent extends RBCBaseEvent {
  version: string;
  type: "view";
  name: "screen_view";
  screen_name: string;
  universalEventParameters: RBCUniversalEventParameters;
}

export type RBCAnalyticsEvent = RBCTapEvent | RBCScreenViewEvent;

export enum RBCEventType {
  TAP = "tap",
  SCREEN_VIEW = "screen_view",
}

/**
 * Determine if an event is a click/tap event
 */
export const isClickEvent = (eventName: string): boolean => {
  return (
    eventName.includes("clicked") ||
    eventName.includes("tapped") ||
    eventName.includes("tap") ||
    eventName.includes("click")
  );
};

/**
 * Determine if an event is a view/screen_view event
 */
export const isViewEvent = (eventName: string): boolean => {
  return (
    eventName.includes("viewed") ||
    eventName.includes("page_view") ||
    eventName.includes("view")
  );
};

/**
 * Determine the RBC event type from event name
 * Returns TAP by default
 */
export const getRBCEventType = (eventName: string): RBCEventType | null => {
  if (isViewEvent(eventName)) return RBCEventType.SCREEN_VIEW;
  return RBCEventType.TAP;
};

/**
 * URL parameters that should NOT be sent to analytics for security/privacy
 */
const SENSITIVE_PARAMS = [
  "hopperUserId",
  "token",
  "apiKey",
  "api_key",
  "password",
  "secret",
  "auth",
  "authorization",
  "session",
  "sessionId",
  "access_token",
  "refresh_token",
];

/**
 * Extract non-sensitive search parameters from current URL
 * Filters out authentication tokens and sensitive data
 */
export const getSearchTermFromURL = (): string | undefined => {
  if (typeof window === "undefined") return undefined;

  const params = new URLSearchParams(window.location.search);
  const searchParams: string[] = [];

  params.forEach((value, key) => {
    // Skip sensitive parameters (exact match to avoid false positives)
    const lowerKey = key.toLowerCase();
    const isSensitive = SENSITIVE_PARAMS.some(
      (sensitive) => lowerKey === sensitive.toLowerCase()
    );

    if (!isSensitive) {
      searchParams.push(`${key}=${value}`);
    }
  });

  return searchParams.length > 0 ? searchParams.join("&") : undefined;
};

/**
 * Create an RBC analytics event from event name and properties
 */
export const createRBCEvent = (
  eventName: string,
  version: string,
  language: string,
  universalEventParameters: RBCUniversalEventParameters,
  properties?: Record<string, any>
): RBCAnalyticsEvent | null => {
  const eventType = getRBCEventType(eventName);

  if (!eventType) {
    return null;
  }

  const baseEvent: RBCBaseEvent = {
    event: eventName,
    page_language: language,
    lob: "avion-web",
    channel: "secure",
    content_group: "avion-web : hopper",
  };

  if (eventType === RBCEventType.TAP) {
    return {
      ...baseEvent,
      version,
      type: "click",
      name: "tap",
      details: `travel - ${eventName}`,
      element_type: properties?.element_type || "button",
      universalEventParameters,
    };
  }

  // SCREEN_VIEW
  return {
    ...baseEvent,
    version,
    type: "view",
    name: "screen_view",
    screen_name: `travel - ${eventName}`,
    universalEventParameters,
  };
};
